import React, { memo, useMemo } from "react";
import { Text, HStack, VStack } from "@chakra-ui/react";
import colors from "app/colors";
import { MessageThreadFragment, MessageThreadType } from "gql/graphql";
import { InfoIcon } from "modules/icons";
import moment from "moment";
import { MESSAGE_CALENDAR_FORMATS } from "modules/utils/Constants";
import { useAuth } from "modules/providers/auth-provider";
import useRemovedFromThread from "modules/hooks/use-removed-from-thread";

interface IProps {
  messageThread: MessageThreadFragment;
  includeDetails?: boolean;
}

const MessageThreadMeRemovedHeader = ({
  messageThread,
  includeDetails = true,
}: IProps) => {
  const { me } = useAuth();
  const { removedUser } = useRemovedFromThread(messageThread);

  const message = useMemo(() => {
    if (!removedUser) return null;
    const removeTime = moment(removedUser.createTime).calendar(
      MESSAGE_CALENDAR_FORMATS,
    );
    switch (messageThread.type) {
      case MessageThreadType.Chat: {
        if (removedUser.removedByUserId === me?.id) {
          return `You left this chat ${removeTime}`;
        }
        return `You were removed from this chat ${removeTime}`;
      }
      case MessageThreadType.Task: {
        if (removedUser.removedByUserId === me?.id) {
          return `You left this task ${removeTime}`;
        }
        return `You have been removed from this task ${removeTime}`;
      }
      case MessageThreadType.Thread: {
        if (removedUser.removedByUserId === me?.id) {
          return `You left this conversation ${removeTime}`;
        }
        return `You were removed from this conversation ${removeTime}`;
      }
      default:
        return `You are no longer a member of this conversation`;
    }
  }, [me?.id, messageThread.type, removedUser]);

  if (!removedUser) {
    return null;
  }

  return (
    <HStack
      w="full"
      backgroundColor={colors.yellow10}
      color={colors.gray50}
      alignItems="flex-start"
      borderRadius={4}
    >
      <InfoIcon boxSize={4} fill={colors.gray50} />
      <VStack w="full" spacing={0} alignItems="flex-start">
        <Text fontSize={12}>{message}</Text>
        {includeDetails && (
          <Text fontSize={12}>
            These details are from the conversation at that time and have not
            been updated.
          </Text>
        )}
      </VStack>
    </HStack>
  );
};

export default memo(MessageThreadMeRemovedHeader);
